import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  BookOpen, FileText, IndianRupee, Wallet, Landmark, BarChart3, Users,
  Calculator, TrendingUp, Shield, Truck, ArrowRight, Play, CheckCircle2,
  Phone, Mail, Building2, Star, Zap, Globe, ChevronDown, Check
} from 'lucide-react';
import api from '../services/api';

import { FlutterFoxFooter } from '../components/FlutterFoxFooter';

const ICON_MAP = { BookOpen, FileText, IndianRupee, Wallet, Landmark, BarChart3, Users, Calculator, TrendingUp, Shield, Truck, Globe, Star, Zap, Building2 };

const container = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

export default function LandingPage() {
  const navigate = useNavigate();
  const [cms, setCms] = useState(null);
  const [leadForm, setLeadForm] = useState({ name: '', phone: '', company: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    api.get('/super-admin/cms').then(r => setCms(r.data)).catch(() => {});
  }, []);

  const hero = cms?.hero || { badge: 'Logistics + Accounting ERP', headline: 'Run Your Entire Logistics Business on Autopilot', highlight_word: 'Autopilot', subheadline: 'Invoices, Ledger, GST, TDS, LR — Everything unified.', cta_primary: 'Start Free Trial', cta_secondary: 'Watch Demo', trust_line: 'Used by growing logistics companies' };
  const stats = cms?.stats || [{ value: '10,000+', label: 'Invoices' }, { value: '100+', label: 'Companies' }, { value: '99.9%', label: 'Uptime' }, { value: '24/7', label: 'Support' }];
  const features = cms?.features || [];
  const pricing = cms?.pricing || [];
  const faqs = cms?.faqs || [];
  const socialProof = cms?.social_proof || [];

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.phone) { toast.error('Name and phone are required'); return; }
    setSubmitting(true);
    try {
      await api.post('/leads', leadForm);
      toast.success('Thank you! We\'ll contact you shortly.');
      setLeadForm({ name: '', phone: '', company: '', email: '' });
    } catch { toast.error('Something went wrong. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const renderHeadline = () => {
    const hl = hero.headline || '';
    const hw = hero.highlight_word || '';
    if (hw && hl.includes(hw)) {
      const parts = hl.split(hw);
      return <>{parts[0]}<span className="text-purple-200">{hw}</span>{parts[1]}</>;
    }
    return hl;
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] overflow-hidden">
      {/* ===== HERO ===== */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 wisteria-gradient-bg opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[hsl(var(--background))]" />

        {/* Floating shapes */}
        <div className="absolute top-20 left-[10%] w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm animate-float border border-white/20" />
        <div className="absolute top-40 right-[15%] w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm animate-float-slow border border-white/20" />
        <div className="absolute bottom-32 left-[20%] w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm animate-float-delay border border-white/20" />
        <div className="absolute top-[60%] right-[8%] w-24 h-24 rounded-3xl bg-white/5 backdrop-blur-sm animate-float border border-white/10" />

        {/* Nav */}
        <div className="absolute top-0 left-0 right-0 z-20 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
              <span className="font-heading font-black text-white text-sm">T</span>
            </div>
            <span className="font-heading font-bold text-white text-lg tracking-tight">Transsolution</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 flex-shrink-0">
            <a href="/about" className="hidden sm:inline-block px-4 py-2 text-white/80 text-sm font-medium hover:text-white transition-colors" data-testid="landing-about-btn">About</a>
            <a href="/contact" className="hidden sm:inline-block px-4 py-2 text-white/80 text-sm font-medium hover:text-white transition-colors" data-testid="landing-contact-btn">Contact</a>
            <button onClick={() => navigate('/login')} className="px-3 sm:px-5 py-1.5 sm:py-2 rounded-full bg-white/25 backdrop-blur-sm text-white text-xs sm:text-sm font-semibold hover:bg-white/40 transition-all border border-white/40 shadow-md whitespace-nowrap" data-testid="landing-login-btn">
              Login
            </button>
          </div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pt-20">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-white/90 text-sm font-bold uppercase tracking-[0.2em] mb-4 hero-subtext-shadow">{hero.badge}</p>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1] mb-6 max-w-4xl mx-auto hero-text-shadow">
              {renderHeadline()}
            </h1>
            <p className="text-white/90 text-base sm:text-lg max-w-2xl mx-auto mb-10 leading-relaxed font-medium hero-subtext-shadow">{hero.subheadline}</p>
            <div className="flex gap-4 justify-center flex-wrap">
              <button onClick={() => navigate('/login')} className="px-8 py-3.5 rounded-full bg-white text-purple-700 font-bold text-sm hover:shadow-xl hover:shadow-purple-500/20 transition-all hover:scale-105" data-testid="hero-start-btn">
                {hero.cta_primary}
              </button>
              <button className="px-8 py-3.5 rounded-full bg-white/10 backdrop-blur-sm text-white font-semibold text-sm border border-white/30 hover:bg-white/20 transition-all flex items-center gap-2" data-testid="hero-demo-btn">
                <Play className="w-4 h-4" /> {hero.cta_secondary}
              </button>
            </div>
          </motion.div>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
            className="text-white/60 text-xs mt-12 uppercase tracking-[0.15em] font-bold hero-subtext-shadow">{hero.trust_line}</motion.p>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="py-12 -mt-16 relative z-10">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s, i) => (
              <motion.div key={i} variants={item} className="glass-card rounded-xl p-5 text-center" data-testid={`stat-${i}`}>
                <p className="text-2xl font-bold font-heading text-purple-700 dark:text-purple-300">{s.value}</p>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-[0.1em] font-medium mt-1">{s.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== PROBLEM ===== */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-purple-500 mb-3">The Problem</p>
            <h2 className="font-heading font-semibold text-2xl sm:text-3xl text-gray-800 dark:text-gray-100 mb-8">Running logistics without a system is chaos</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left max-w-2xl mx-auto">
              {['Scattered Excel files', 'Manual GST & TDS mistakes', 'No clarity on profit per shipment', 'Advance payments getting messed up', 'Duplicate ledger entries', 'No real-time financial visibility'].map((p, i) => (
                <div key={i} className="flex items-center gap-2.5 text-sm text-gray-600 dark:text-gray-400">
                  <span className="w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0"><span className="text-red-500 text-xs font-bold">x</span></span>
                  {p}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="py-16 bg-gradient-to-b from-purple-50/50 to-transparent dark:from-purple-950/10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-purple-500 mb-3">Features</p>
            <h2 className="font-heading font-semibold text-2xl sm:text-3xl text-gray-800 dark:text-gray-100">Everything you need in one system</h2>
          </div>
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => {
              const IconComp = ICON_MAP[f.icon] || Star;
              return (
              <motion.div key={i} variants={item} className="glass-card rounded-xl p-5 hover:shadow-lg hover:shadow-purple-100 dark:hover:shadow-purple-900/20 transition-all duration-300 hover:-translate-y-1 group" data-testid={`feature-card-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <IconComp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="font-heading font-semibold text-sm text-gray-800 dark:text-gray-100 mb-1">{f.title}</h3>
                <p className="text-[12px] text-gray-500 dark:text-gray-400 leading-relaxed">{f.desc}</p>
              </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ===== WHY TRANSSOLUTION ===== */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-purple-500 mb-3">Why Transsolution</p>
            <h2 className="font-heading font-semibold text-2xl sm:text-3xl text-gray-800 dark:text-gray-100">Not just software — a complete business system</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { feat: 'Logistics + Accounting unified', icon: Zap },
              { feat: 'Busy-like manual control', icon: Calculator },
              { feat: 'AI Accounting Assistant', icon: Star },
              { feat: 'Data Import Automation', icon: FileText },
              { feat: 'Multi-company SaaS', icon: Building2 },
              { feat: 'Real-time Analytics', icon: TrendingUp },
            ].map((w, i) => (
              <div key={i} className="flex items-center gap-3 p-4 glass-card rounded-xl">
                <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shrink-0">
                  <w.icon className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{w.feat}</span>
                <CheckCircle2 className="w-4 h-4 text-emerald-500 ml-auto shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      {pricing.length > 0 && (
        <section className="py-16" id="pricing">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-10">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-purple-500 mb-3">Pricing</p>
              <h2 className="font-heading font-semibold text-2xl sm:text-3xl text-gray-800 dark:text-gray-100">Simple, transparent pricing</h2>
            </div>
            <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {pricing.map((plan, i) => (
                <motion.div key={i} variants={item}
                  className={`glass-card rounded-2xl p-6 relative ${plan.popular ? 'ring-2 ring-purple-500 shadow-lg shadow-purple-200 dark:shadow-purple-900/30' : ''}`}
                  data-testid={`pricing-card-${i}`}>
                  {plan.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-purple-600 to-purple-400 text-white text-[10px] font-bold uppercase tracking-wider">Most Popular</span>}
                  <h3 className="font-heading font-semibold text-lg text-gray-800 dark:text-gray-100 mb-1">{plan.name}</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold font-heading text-purple-700 dark:text-purple-300">Rs.{plan.price}</span>
                    <span className="text-gray-400 text-sm">/{plan.period}</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {(plan.features || []).map((f, fi) => (
                      <li key={fi} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Check className="w-4 h-4 text-purple-500 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => navigate('/login')} className={plan.popular ? 'btn-primary w-full py-2.5' : 'btn-secondary w-full py-2.5'}>
                    Get Started
                  </button>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ===== FAQ ===== */}
      {faqs.length > 0 && (
        <section className="py-16 bg-gradient-to-b from-purple-50/30 to-transparent dark:from-purple-950/10">
          <div className="max-w-2xl mx-auto px-6">
            <div className="text-center mb-10">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-purple-500 mb-3">FAQ</p>
              <h2 className="font-heading font-semibold text-2xl text-gray-800 dark:text-gray-100">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-2">
              {faqs.map((faq, i) => (
                <div key={i} className="glass-card rounded-xl overflow-hidden" data-testid={`faq-${i}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full px-5 py-3.5 flex items-center justify-between text-left text-sm font-medium text-gray-800 dark:text-gray-200 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 transition">
                    {faq.question}
                    <ChevronDown className={`w-4 h-4 text-purple-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  {openFaq === i && <div className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400">{faq.answer}</div>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== LEAD CAPTURE ===== */}
      <section className="py-16 bg-gradient-to-b from-purple-50/50 to-transparent dark:from-purple-950/10" id="demo">
        <div className="max-w-lg mx-auto px-6">
          <div className="text-center mb-8">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-purple-500 mb-3">Get Started</p>
            <h2 className="font-heading font-semibold text-2xl text-gray-800 dark:text-gray-100 mb-2">Book a Free Demo</h2>
            <p className="text-sm text-gray-500">See how Transsolution can increase your profit clarity by 3X</p>
          </div>
          <form onSubmit={handleLeadSubmit} className="glass-card rounded-2xl p-6 space-y-3" data-testid="lead-form">
            <input type="text" placeholder="Your Name *" value={leadForm.name} onChange={e => setLeadForm(f => ({...f, name: e.target.value}))}
              className="form-input" data-testid="lead-name" />
            <input type="tel" placeholder="Phone Number *" value={leadForm.phone} onChange={e => setLeadForm(f => ({...f, phone: e.target.value}))}
              className="form-input" data-testid="lead-phone" />
            <input type="text" placeholder="Company Name" value={leadForm.company} onChange={e => setLeadForm(f => ({...f, company: e.target.value}))}
              className="form-input" data-testid="lead-company" />
            <input type="email" placeholder="Email" value={leadForm.email} onChange={e => setLeadForm(f => ({...f, email: e.target.value}))}
              className="form-input" data-testid="lead-email" />
            <button type="submit" disabled={submitting} className="btn-primary w-full py-3 text-sm" data-testid="lead-submit">
              {submitting ? 'Submitting...' : 'Book Demo'}
            </button>
          </form>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-16 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="font-heading font-semibold text-2xl sm:text-3xl text-gray-800 dark:text-gray-100 mb-4">Stop managing chaos. Start running a system.</h2>
          <div className="flex gap-4 justify-center">
            <button onClick={() => navigate('/login')} className="btn-primary px-8 py-3" data-testid="final-cta-trial">Start Free Trial</button>
            <a href="#demo" className="btn-secondary px-8 py-3">Book Demo</a>
          </div>
        </motion.div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-purple-100 dark:border-purple-900/30 py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[11px] text-gray-400 dark:text-gray-600 uppercase tracking-[0.1em]">&copy; 2026 Transsolution ERP. All Rights Reserved.</p>
              <div className="flex gap-4 mt-2">
                <a href="/about" className="text-[11px] text-gray-400 hover:text-purple-500 transition-colors">About Us</a>
                <a href="/contact" className="text-[11px] text-gray-400 hover:text-purple-500 transition-colors">Contact Us</a>
              </div>
            </div>
            <FlutterFoxFooter variant="purple" testId="landing-footer-flutterfox" className="text-[11px] text-gray-400 dark:text-gray-500" />
          </div>
        </div>
      </footer>
    </div>
  );
}
